import MomentUtils from "@date-io/moment";
import { AppBar, Divider, Drawer, Hidden, IconButton, Theme, Toolbar, Container } from '@material-ui/core';
import { Menu } from '@material-ui/icons';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { createStyles, makeStyles } from '@material-ui/styles';
import { DFSValueType, FormDefinitionWrapper, DFSExpression, NIFormData } from '../services';
import { ErrorObject } from 'ajv';
import json_pointer from 'json-pointer';
import * as moment from 'moment';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { combineLatest, from, merge, Observable, of } from 'rxjs';
import { delay, filter, map, withLatestFrom } from 'rxjs/operators';
import { setActiveDefinition } from '../../../../redux';
import { IFormContext, FormContext, ImageService } from '../FormContext';
import { ExpressionManager } from '../services/ExpressionManager';
import { ValidityManager } from '../services/ValidityManager';
import { VisibilityManager } from '../services/VisibilityManager';
import { resolveRefs } from '../utils';
import LoadingIndicator from './LoadingIndicator';
import { Page } from './Page';
import { TableOfContents } from './TableOfContents';
import EvalOverlay from '../../EvalOverlay';


export interface FormWrapperProps {
  form: FormDefinitionWrapper;
  data$: Observable<NIFormData>;
  evalID?: string;
  canEdit?: boolean;
  onSetData: (path: string, scope: string, newData: DFSValueType | null) => void;
  onRemoveData: (path: string, scope: string) => void;
  onErrors?: (errors: ErrorObject[]) => void;
  compressedToCHeader?: JSX.Element;
  tocFooter?: JSX.Element;
  imageService: ImageService;
}

export interface FormProps extends Omit<FormWrapperProps, 'form'> {
  form: FormDefinitionWrapper;
}

const drawerWidth = '240px';

const useStyles = makeStyles(function(theme: Theme) {
  return createStyles({
    root: {
      flex: 1,
      height: '100%',
      display: 'flex',
      overflow: 'auto',
      alignContent: 'stretch',
      backgroundColor: theme.palette.background.default,
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row'
      }
    },
    toolbar: {},
    menuButton: {
      color: theme.palette.getContrastText(theme.palette.primary.main)
    },
    drawer: {
      flexBasis: drawerWidth,
      zIndex: 0,
      height: '100%'
    },
    paper: {
      position: 'relative',
      top: 'inherit',
      left: 'inherit',
      bottom: 0,
      padding: 0,
      width: drawerWidth,
      zIndex: 1,
      borderRight: 0
    },
    drawerRoot: {
      display: 'flex',
      height: '100%',
      flexDirection: 'column'
    },
    tocHeader: {
      backgroundColor: theme.palette.primary.main
    },
    navFooter: {
      display: 'flex',
      flexDirection: 'column'
    },
    page: {
      flex: 1,
      overflowY: 'scroll' as 'scroll',
      WebkitOverflowScrolling: 'touch',
      alignSelf: 'stretch',
      display: 'flex',
      flexDirection: 'column'
    }
  })
});

// todo use either redux or form context for active definition - not both
export const FormWrapper: FC<FormWrapperProps> = function(props) {
  const [form, setForm] = useState<FormDefinitionWrapper | undefined>(undefined);

  const dispatch = useDispatch();

  useEffect(function r() {
    if (props.form === undefined || props.form === null) {
      return;
    }

    const { definition, ...descriptorOnly } = props.form;
    dispatch(setActiveDefinition(descriptorOnly));

    const subscription = from(resolveRefs(props.form))
      .subscribe(function(form: any) {
        setForm(form);
      });

    return function() {
      dispatch(setActiveDefinition(null));
      return subscription.unsubscribe();
    }
  }, [props.form]);

  return (
    form === undefined || !props.form
      ? <LoadingIndicator>Loading...</LoadingIndicator>
      : <Form
          {...props}
          form = { form }>
        </Form>
  )
}

// todo memoize this form via react.memo, remove useMemo from eval editor/follow up editor, move footer into own component and use directly here rather than passing as prop
const Form: FC<FormProps> = function(props: FormProps) {

  const { form, data$, onSetData, onRemoveData, evalID, imageService, canEdit } = props;
  const root = form.definition.render.editor;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentCard, setCurrentCard] = useState(0);
  const [pageOffsets, setPageOffsets] = useState<number[]>([]);

  const [pages, setPages] = useState(
    root.pages.map((def, idx) => ({ definition: def, index: idx, isVisible: true }))
  )

  const classes = useStyles({});

  const expMgr = useMemo(function() {
    return new ExpressionManager(data$);
  }, [data$]);

  const visibilityMgr = useMemo(function() {
    return new VisibilityManager(form.definition, expMgr);
  }, [form.id, expMgr]);

  const validityMgr = useMemo(function() {
    return new ValidityManager(form.definition, expMgr, data$ as any);
  }, [form.id, expMgr, data$]);

  useEffect(function() {
    return () => {
      expMgr.complete();
      visibilityMgr.complete();
      validityMgr.reset();
    }
  }, []);

  useEffect(function watchPageVisibility() {
    const subscription = combineLatest(root.pages.map((definition, index) => {
      const condition = definition.visibility ?? definition.visible;
      return condition !== undefined
        ? expMgr!.watch(condition)
            .pipe(
              map(isVisible => {
                return { definition, index, isVisible };
              })
            )
        : of({ definition, index, isVisible: true });
    }))
    .subscribe((pages: any[]) => {
      const hiddenPages: number[] = [];
      pages.forEach((page, i) => {
        if (!page.isVisible) {
          hiddenPages.push(i);
        }
      })
      setPageOffsets(hiddenPages);
      return setPages(pages);
    });

    return () => {
      return subscription.unsubscribe();
    };
  }, [expMgr, root.pages]);

  useEffect(function e() {

    if (!json_pointer.has(form.definition, '/hooks/edit/computedValues')) {
      return;
    }

    var computers = Object.entries(form.definition.hooks!.edit!.computedValues!).map(function(computedFields) {
      // why are these even being triggered on publish? nothing changes
      let [dataPath, exp] = computedFields as [ string, DFSExpression ];
      return expMgr.watch(exp).pipe(
        withLatestFrom(expMgr.watch(dataPath)),
        filter(function (e) {
          // [next, current]
          return e[0] !== e[1];
        }),
        map(function (e) {
          const [expResult, _] = e;
          return [dataPath, expResult];
        })
      );
    });

    // should compare to current (synchronous) eval value, not necessarily latest path?
    const subscription = merge(...computers)
      .pipe(delay(1))
      .subscribe(function(e) {
        const [ dataPath, val ] = e as [ string, any ];

        if (val !== undefined && val !== null) {
          onSetData(dataPath, '', val);
        }

        else {
          onRemoveData(dataPath, '');
        }
      });

      return function() {
        return subscription.unsubscribe()
      }
  }, [expMgr, form.id, onSetData, onRemoveData, form.definition]);

  const [isDeskAudit, setIsDeskAudit] = React.useState(false);
  const [isFocusedAudit, setIsFocusedAudit] = React.useState(false);
  const [isReadonly, setIsReadonly] = React.useState(false);

  const toggleDeskAudit = () => {
    setIsDeskAudit(!isDeskAudit);
  };
  const toggleFocusedAudit = () => {
    setIsFocusedAudit(!isFocusedAudit);
  };

  const formContext:IFormContext = { form, expMgr, visibilityMgr, validityMgr, onSetData, onRemoveData, scope: '', evalID, imageService, isDeskAudit, toggleDeskAudit, isFocusedAudit, toggleFocusedAudit, isReadonly, setIsReadonly };

  useEffect(() => {
    if (canEdit || canEdit === false) {
      setIsReadonly(!canEdit);
    }
  }, [canEdit]);

  useEffect(function r() {
    if (!props.onErrors) {
      return;
    }
    const subscription = formContext.validityMgr!.watch('').subscribe(function(r) {
      props.onErrors!(r)
    });
    return function() {
      return subscription.unsubscribe()
    }
  }, [props.onErrors, formContext.validityMgr]);

  // if for some reason a page that is out of range is selected, just go to the first page.
  useEffect(() => {
    if (root.pages && !root.pages[currentPage] && currentPage !== 0) {
      setCurrentPage(0);
    }
  }, [root, currentPage])

  const onPageSelected = (page: number) => {
    setCurrentPage(page);
    setCurrentCard(-1);
    handleDrawerToggle();
  };

  const onCardSelected = (card: number) => {
    setCurrentCard(card);
    handleDrawerToggle();
  };

  const toc = useMemo(function() {
    return <TableOfContents
      pages = { pages }
      currentPage = { currentPage }
      currentCard = { currentCard }
      onPageSelected = { onPageSelected }
      onCardSelected = { onCardSelected }>
      </TableOfContents>
  }, [root.pages, currentPage, currentCard, onPageSelected, onCardSelected]);

  const footer = <div className = { classes.navFooter }>
    { props.tocFooter &&
      <React.Fragment>
        <Divider variant = { 'fullWidth' }></Divider>
        { props.tocFooter }
      </React.Fragment>
    }
  </div>;

  const handleDrawerToggle = useMemo(function() {
    return function() {
      return setDrawerOpen(!drawerOpen)
    }
  }, [drawerOpen]);

  return (
    <FormContext.Provider value = { formContext }>
      <EvalOverlay></EvalOverlay>
      <MuiPickersUtilsProvider libInstance = { moment } utils = { MomentUtils }>
        <div className = { classes.root }>
          <Hidden smUp>
            <AppBar className = { classes.toolbar }>
              <Toolbar>
                <IconButton onClick = { handleDrawerToggle }>
                  <Menu color = { 'inherit' } className = { classes.menuButton } />
                </IconButton>
              </Toolbar>
            </AppBar>
            <Drawer
              className = { classes.drawer }
              variant = { 'temporary' }
              classes = { { paper: classes.paper } }
              anchor = { 'left' }
              open = { drawerOpen }
              onClose = { handleDrawerToggle }
              ModalProps = { { keepMounted: true } }>
              <div className = { classes.drawerRoot }>
                <div style = { { overflow: 'scroll' } }>
                  { props.compressedToCHeader &&
                    <div className = { classes.tocHeader }>
                      { props.compressedToCHeader }
                    </div>
                  }
                  { toc }
                </div>
                { footer }
              </div>
            </Drawer>
          </Hidden>

          <Hidden xsDown implementation = { 'css' }>
            <Drawer
              className = { classes.drawer }
              variant = { 'permanent' }
              classes = { { paper: classes.paper } }>
              { toc }
              { footer }
            </Drawer>
          </Hidden>
          <div className = { classes.page }>
            <Page
              path = { '/render/editor/pages/' + currentPage }
              definition = { root.pages[currentPage] }
              activeCard = { currentCard }
              page = { currentPage }
              pageCount = { root.pages.length }
              pages = { pages }
              pageOffsets = { pageOffsets }
              onChangePage = { e => {
                setCurrentPage(e);
                setCurrentCard(-1);
              } }
              ></Page>
          </div>
        </div>
      </MuiPickersUtilsProvider>
    </FormContext.Provider>
  )
};
